import { useState, useEffect, useRef, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { MfaCodeInput } from '@/components/auth';
import { settingsApi } from '@/services/settings';
import { ApiClientError } from '@/services/api';

export interface SmsMfaSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  totpEnabled: boolean;
}

type SetupStep = 'phone' | 'verify' | 'success';

// US phone number formatting
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits (US number without country code)
  const limited = digits.slice(0, 10);

  // Format as (XXX) XXX-XXXX
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Convert formatted number to E.164 format
const toE164 = (formattedNumber: string): string => {
  const digits = formattedNumber.replace(/\D/g, '');
  return `+1${digits}`;
};

/**
 * Modal for SMS MFA setup flow
 * 1. Enter phone number with consent checkbox
 * 2. Verify with 6-digit SMS code
 * 3. Success confirmation
 */
export default function SmsMfaSetupModal({
  isOpen,
  onClose,
  onSuccess,
  totpEnabled,
}: SmsMfaSetupModalProps) {
  const [step, setStep] = useState<SetupStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneLastFour, setPhoneLastFour] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resend countdown timer
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Clear countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Start resend countdown
  const startResendCountdown = (seconds: number = 60) => {
    setResendCountdown(seconds);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle phone number submission
  const handlePhoneSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!consentChecked) {
      setError('Please consent to receive SMS messages');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await settingsApi.setupSmsMfa({
        phoneNumber: toE164(phoneNumber),
        consentGiven: true,
      });

      setPhoneLastFour(response.phoneLastFour);
      startResendCountdown();
      setStep('verify');
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) {
          const retryAfter = err.retryAfter || 60;
          setError(`Please wait ${retryAfter} seconds before requesting another code`);
        } else if (err.status === 503) {
          setError('SMS service is temporarily unavailable. Please try again later.');
        } else {
          setError(err.detail);
        }
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle verification code submission
  const handleVerify = async (e?: FormEvent) => {
    e?.preventDefault();

    if (verificationCode.length !== 6) return;

    setIsLoading(true);
    setError(null);

    try {
      await settingsApi.verifySmsMfaSetup({ code: verificationCode });
      setStep('success');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Invalid code. Please try again.');
      }
      setVerificationCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle code input completion
  const handleCodeComplete = (code: string) => {
    if (code.length === 6) {
      // Submit automatically
      setIsLoading(true);
      setError(null);
      settingsApi
        .verifySmsMfaSetup({ code })
        .then(() => {
          setStep('success');
        })
        .catch((err) => {
          if (err instanceof ApiClientError) {
            setError(err.detail);
          } else {
            setError('Invalid code. Please try again.');
          }
          setVerificationCode('');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // Handle resend code
  const handleResend = async () => {
    if (resendCountdown > 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await settingsApi.resendSmsMfaCode();
      setPhoneLastFour(response.phoneLastFour);
      startResendCountdown();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) {
          const retryAfter = err.retryAfter || 60;
          setError(`Please wait ${retryAfter} seconds before requesting another code`);
          startResendCountdown(retryAfter);
        } else {
          setError(err.detail);
        }
      } else {
        setError('Failed to resend code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep('phone');
    setPhoneNumber('');
    setConsentChecked(false);
    setVerificationCode('');
    setPhoneLastFour('');
    setError(null);
    setResendCountdown(0);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  // Check prerequisite: TOTP must be enabled
  if (!totpEnabled) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-mfa-modal-title"
      >
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
          aria-hidden="true"
        />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
          <h2 id="sms-mfa-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
            SMS MFA Setup
          </h2>
          <Alert variant="warning" className="mb-4">
            SMS MFA requires an authenticator app as primary method.
            Please set up TOTP first before enabling SMS as a backup.
          </Alert>
          <Button onClick={handleClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sms-mfa-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={step === 'phone' || step === 'success' ? handleClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 id="sms-mfa-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
          {step === 'phone' && 'Set Up SMS Backup'}
          {step === 'verify' && 'Verify Your Phone'}
          {step === 'success' && 'SMS MFA Enabled'}
        </h2>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Phone Input */}
        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit}>
            <p className="text-sm text-gray-600 mb-4">
              Add your phone number as a backup authentication method.
              You can use SMS codes when you cannot access your authenticator app.
            </p>

            <div className="mb-4">
              <Input
                label="Phone Number (US)"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                placeholder="(555) 123-4567"
                autoComplete="tel-national"
                autoFocus
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                US phone numbers only. VoIP numbers are not supported.
              </p>
            </div>

            {/* TCPA Consent Checkbox */}
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-600">
                  I consent to receive authentication codes via SMS to this phone number.
                  Standard message rates may apply. Reply STOP to disable.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={phoneNumber.replace(/\D/g, '').length !== 10 || !consentChecked}
                isLoading={isLoading}
                className="flex-1"
              >
                Send Code
              </Button>
            </div>
          </form>
        )}

        {/* Step 2: Verification */}
        {step === 'verify' && (
          <form onSubmit={handleVerify}>
            <p className="text-sm text-gray-600 mb-2">
              Enter the 6-digit code sent to:
            </p>
            <p className="text-lg font-medium text-gray-900 mb-4">
              ***-***-{phoneLastFour}
            </p>

            {isLoading && step === 'verify' && !verificationCode ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <MfaCodeInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    onComplete={handleCodeComplete}
                    disabled={isLoading}
                    error={error ? ' ' : undefined}
                    autoFocus
                  />
                </div>

                <div className="flex justify-center mb-6">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCountdown > 0 || isLoading}
                    className="text-sm text-primary-600 hover:text-primary-500 disabled:text-gray-400"
                  >
                    {resendCountdown > 0
                      ? `Resend code in ${resendCountdown}s`
                      : 'Resend code'}
                  </button>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep('phone');
                      setVerificationCode('');
                      setError(null);
                    }}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={verificationCode.length !== 6}
                    isLoading={isLoading}
                    className="flex-1"
                  >
                    Verify
                  </Button>
                </div>
              </>
            )}
          </form>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div>
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center mb-4">
              SMS MFA has been enabled for your account. You can now use SMS codes
              as a backup when signing in.
            </p>

            <p className="text-sm text-gray-500 text-center mb-6">
              Phone: ***-***-{phoneLastFour}
            </p>

            <Button onClick={handleDone} className="w-full">
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
